/**
 * <cart-upsells data-product-id="123"> — the "Frequently Bought with" rail in
 * the cart drawer.
 *
 * The drawer re-renders by replacing .drawer__inner's innerHTML, which
 * destroys and recreates this element. Custom elements upgrade on insertion,
 * so connectedCallback fires again on every cart update and the rail
 * refetches itself. Results are cached per seed product so repeated cart
 * changes don't re-hit the recommendations endpoint.
 */
if (!customElements.get("cart-upsells")) {
	// Keyed by seed product id, shared across every instance of the element.
	const railCache = new Map()

	customElements.define(
		"cart-upsells",
		class CartUpsells extends HTMLElement {
			connectedCallback() {
				this.addEventListener("click", this.onClick.bind(this))

				const productId = this.dataset.productId
				if (!productId) return

				this.load(productId)
			}

			async load(productId) {
				const cached = railCache.get(productId)
				if (cached !== undefined) {
					this.inject(cached)
					return
				}

				// "complementary" is what merchandisers curate in Search & Discovery
				// and is the better match for "Frequently Bought with". It returns
				// nothing when nothing has been curated, so fall back to the
				// algorithmic "related" set.
				let html = await this.fetchRail(productId, "complementary")
				if (!html) html = await this.fetchRail(productId, "related")

				railCache.set(productId, html || "")
				this.inject(html || "")
			}

			async fetchRail(productId, intent) {
				const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/"
				const url = `${root}recommendations/products?section_id=cart-upsells&product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`

				try {
					const response = await fetch(url)
					if (!response.ok) return null

					const rail = new DOMParser().parseFromString(await response.text(), "text/html").querySelector(".cart-upsells")

					return rail ? rail.outerHTML : null
				} catch (error) {
					return null
				}
			}

			inject(html) {
				this.innerHTML = html
				this.toggleAttribute("hidden", !html)
			}

			onClick(event) {
				const button = event.target.closest(".cart-upsells__add")
				if (!button) return

				event.preventDefault()
				this.addToCart(button)
			}

			async addToCart(button) {
				const variantId = button.dataset.variantId
				const drawer = document.querySelector("cart-drawer")

				if (!variantId || !drawer || button.getAttribute("aria-disabled") === "true") return

				button.setAttribute("aria-disabled", "true")
				button.classList.add("loading")

				// Mirrors product-form.js: ask /cart/add.js to return the rendered
				// drawer sections so we can hand them straight to renderContents.
				const formData = new FormData()
				formData.append("id", variantId)
				formData.append("quantity", 1)
				formData.append("sections", drawer.getSectionsToRender().map((section) => section.id))
				formData.append("sections_url", window.location.pathname)

				const config = fetchConfig("javascript")
				config.headers["X-Requested-With"] = "XMLHttpRequest"
				delete config.headers["Content-Type"]
				config.body = formData

				try {
					const response = await fetch(routes.cart_add_url, config)
					const parsedState = await response.json()

					if (parsedState.status) {
						this.resetButton(button)
						return
					}

					drawer.setActiveElement(document.activeElement)
					drawer.renderContents(parsedState)
				} catch (error) {
					this.resetButton(button)
				}
			}

			resetButton(button) {
				button.removeAttribute("aria-disabled")
				button.classList.remove("loading")
			}
		}
	)
}
